import { firstValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { AuthorizedHttpClient } from "../../01_core/tools/authorize-http";
import { Message } from "@aws-sdk/client-bedrock-runtime";

const maxExtractedSuggestions = 5;

export async function extractSuggestionsFromAssistantMessage(messageText: string, authorizedHttp: AuthorizedHttpClient): Promise<{ messageText: string, suggestions: string[] }> {
  const trimmedMessage = (messageText || '').trim();
  if (!trimmedMessage) {
    return { messageText: '', suggestions: [] };
  }

  const extractionInstruction = `You extract follow-up suggestions from assistant messages.

Input:
The user message contains one assistant message text.

Task:
- Detect short actionable suggestions in the message.
- Remove detected suggestions from messageText.
- Keep only non-empty suggestions.
- Remove duplicates (case-insensitive).
- Keep the original order.
- Return at most 5 suggestions.

Output format:
Return only valid JSON with this exact schema:
{"messageText": "string", "suggestions": ["string"]}

Do not include markdown, explanation, or code fences.`;

  const requestBody = {
    messages: <Message[]>[
      {
        role: 'user',
        content: [{ text: trimmedMessage }],
      },
    ],
    system: [{ text: extractionInstruction }],
  };

  const response = await firstValueFrom(
    authorizedHttp.post<any>(`${environment.baseUrl}/bedrock/gpt-oss-20b`, requestBody)
  );

  const content = response.output?.message?.content || [];
  const rawExtraction = content.filter((c: any) => c.text).map((c: any) => c.text).join('\n').trim();
  const parsedExtraction = parseSuggestionExtraction(rawExtraction);

  const normalizedSuggestions = normalizeSuggestions(parsedExtraction.suggestions);
  const cleanedMessage = (parsedExtraction.messageText || trimmedMessage).trim();

  return {
    messageText: cleanedMessage,
    suggestions: normalizedSuggestions,
  };
}

function parseSuggestionExtraction(rawExtraction: string): { messageText: string, suggestions: string[] } {
  const fallback = { messageText: '', suggestions: [] as string[] };
  if (!rawExtraction) {
    return fallback;
  }

  // Even though prompted otherwise, the model might still include markdown code fences or other text. This cleaning step tries to extract the JSON object from the response in a robust way, but may fail of course.
  const cleaned = rawExtraction
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```$/i, '')
    .trim();

  const tryParse = (value: string) => {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  let parsed = tryParse(cleaned);
  if (!parsed) {
    const objectMatch = cleaned.match(/\{[\s\S]*\}/);
    if (objectMatch) {
      parsed = tryParse(objectMatch[0]);
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    return fallback;
  }

  const messageText = typeof (parsed as any).messageText === 'string' ? (parsed as any).messageText : '';
  const suggestions = Array.isArray((parsed as any).suggestions) ? (parsed as any).suggestions : [];

  return { messageText, suggestions };
}

function normalizeSuggestions(suggestions: string[]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const suggestion of suggestions) {
    if (typeof suggestion !== 'string') {
      continue;
    }

    // Normalize whitespace and trim
    const normalized = suggestion.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      continue;
    }

    // Check for duplicates in a case-insensitive way
    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(normalized);

    if (result.length >= maxExtractedSuggestions) {
      break;
    }
  }

  return result;
}
